import { format, Locale } from "date-fns"
import { ActivityFeedFilters, CommunityActivity } from "@src/api/activityFeed"

export const ACTIVITY_CATEGORIES = [
  "Members",
  "Messages",
  "Home",
  "Startups",
  "Events",
  "Forum",
  "Resources",
  "Requests",
  "Coaching",
  "Pages",
  "Applications",
] as const

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number]

const isCategory = (value: unknown): value is ActivityCategory =>
  ACTIVITY_CATEGORIES.includes(value as ActivityCategory)

const toList = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [value]

const toId = (value: unknown) => {
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : undefined
}

export const parseActivityFeedSearch = (
  search: Record<string, unknown>,
): ActivityFeedFilters => {
  const category = toList(search.category).filter(isCategory)
  const tag = toList(search.tag).filter(
    (value): value is string => typeof value === "string" && value !== "",
  )
  return {
    category: category.length ? category : undefined,
    userId: toId(search.userId),
    projectId: toId(search.projectId),
    programId: toId(search.programId),
    tag: tag.length ? tag : undefined,
  }
}

export const hasActiveFilters = (filters: ActivityFeedFilters) =>
  Object.values(filters).some((value) => value !== undefined)

export type ActivityGroup = {
  key: number
  items: CommunityActivity[]
}

export const groupActivities = (
  activities: CommunityActivity[],
): ActivityGroup[] =>
  activities.reduce<ActivityGroup[]>((groups, activity) => {
    const last = groups[groups.length - 1]
    if (activity.batchId && last?.items[0]?.batchId === activity.batchId) {
      last.items.push(activity)
    } else {
      groups.push({ key: activity.id ?? groups.length, items: [activity] })
    }
    return groups
  }, [])

export type EntityRoute =
  | { kind: "user"; userId: number }
  | { kind: "project"; projectId: number }
  | { kind: "events" }
  | { kind: "forum"; discussionId: number }
  | { kind: "homePost"; commentId: number }
  | { kind: "program"; programId: number }
  | { kind: "resources" }
  | { kind: "request"; projectId: number; requestId: number }
  | { kind: "projectForum"; projectId: number; discussionId: number }
  | { kind: "projectResources"; projectId: number }

const getStartupScopedRoute = (
  type: string,
  id: number,
  projectId: number,
): EntityRoute | null => {
  switch (type) {
    case "Request":
    case "Coaching":
      return { kind: "request", projectId, requestId: id }
    case "ProjectForum":
      return { kind: "projectForum", projectId, discussionId: id }
    case "ProjectResource":
      return { kind: "projectResources", projectId }
    default:
      return null
  }
}

export const getEntityRoute = (
  type: string | null | undefined,
  id: number | null | undefined,
  exists: boolean | null | undefined,
  activity: CommunityActivity,
): EntityRoute | null => {
  if (!type || !id || exists === false) return null

  switch (type) {
    case "User":
      return { kind: "user", userId: id }
    case "Project":
      return { kind: "project", projectId: id }
    case "Event":
      return { kind: "events" }
    case "Forum":
      return { kind: "forum", discussionId: id }
    case "Comment":
      return { kind: "homePost", commentId: id }
    case "Program":
      return { kind: "program", programId: id }
    case "Resource":
      return { kind: "resources" }
    default:
      return activity.projectId
        ? getStartupScopedRoute(type, id, activity.projectId)
        : null
  }
}

export const formatActivityDate = (date: string | undefined, locale: Locale) =>
  date ? format(new Date(date), "d MMM yyyy", { locale }) : ""

export type EntityRef = {
  name: string
  route: EntityRoute | null
}

export type ActivityParties = {
  actor: EntityRef | null
  targets: EntityRef[]
  secondary: EntityRef | null
  program: EntityRef | null
}

const getTargetRef = (activity: CommunityActivity): EntityRef | null =>
  activity.targetEntityName
    ? {
        name: activity.targetEntityName,
        route: getEntityRoute(
          activity.targetEntityType,
          activity.targetEntityId,
          activity.targetExists,
          activity,
        ),
      }
    : null

export const getActivityParties = (
  items: CommunityActivity[],
): ActivityParties => {
  const [first] = items
  const targets = new Map<string, EntityRef>()
  items.forEach((item) => {
    const target = getTargetRef(item)
    const key = `${item.targetEntityType}:${item.targetEntityId ?? target?.name}`
    if (target && !targets.has(key)) targets.set(key, target)
  })

  return {
    actor: first.actorName
      ? {
          name: first.actorName,
          route: getEntityRoute(
            "User",
            first.actorUserId,
            first.actorExists,
            first,
          ),
        }
      : null,
    targets: [...targets.values()],
    secondary: first.secondaryEntityName
      ? {
          name: first.secondaryEntityName,
          route: getEntityRoute(
            first.secondaryEntityType,
            first.secondaryEntityId,
            first.secondaryExists,
            first,
          ),
        }
      : null,
    program: first.programName
      ? {
          name: first.programName,
          route: getEntityRoute("Program", first.programId, null, first),
        }
      : null,
  }
}
